import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { createOrgAddressBook } from "js/Api/address_book";
import ClassificationSelect from "./ClassificationSelect";

const MAX_NAME_LENGTH = 80;

function AddBookModal({ orgId, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [classification, setClassification] = useState("safe");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            toast.error("Please enter a name for the address book.");
            return;
        }
        if (trimmed.length > MAX_NAME_LENGTH) {
            toast.error(`Name must be ${MAX_NAME_LENGTH} characters or fewer.`);
            return;
        }
        setSaving(true);
        const res = await createOrgAddressBook(orgId, { name: trimmed, classification });
        setSaving(false);
        if (res.success) {
            toast.success("Address book created");
            onCreated(res.data);
            onClose();
        } else {
            toast.error(res.error || "Failed to create address book");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !saving) handleSave();
        if (e.key === "Escape" && !saving) onClose();
    };

    const charsLeft = MAX_NAME_LENGTH - name.length;
    const isNearLimit = charsLeft <= 15;

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.45)",
                zIndex: 1050,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            onClick={() => !saving && onClose()}
        >
            <div
                style={{
                    background: "#fff",
                    borderRadius: 16,
                    width: 460,
                    maxWidth: "92vw",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
                    overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ padding: "28px 28px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>
                            New Address Book
                        </h3>
                        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>
                            Give your book a clear, descriptive name.
                        </p>
                    </div>
                    <button
                        onClick={() => !saving && onClose()}
                        style={{ background: "none", border: "none", fontSize: 22, color: "#aaa", cursor: "pointer", lineHeight: 1, padding: 0 }}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: "24px 28px" }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#333", display: "block", marginBottom: 6 }}>
                        Book name <span style={{ color: "#e53e3e" }}>*</span>
                    </label>
                    <input
                        ref={inputRef}
                        type="text"
                        value={name}
                        maxLength={MAX_NAME_LENGTH}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={saving}
                        placeholder="e.g. Emergency Contacts"
                        style={{
                            width: "100%",
                            padding: "10px 14px",
                            border: "1.5px solid #e0e0e0",
                            borderRadius: 8,
                            fontSize: 15,
                            outline: "none",
                            boxSizing: "border-box",
                            transition: "border-color 0.15s",
                        }}
                        onFocus={(e) => e.target.style.borderColor = "#1976d2"}
                        onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5 }}>
                        <span style={{ fontSize: 12, color: "#aaa" }}>Press Enter to create</span>
                        <span style={{ fontSize: 12, color: isNearLimit ? "#e53e3e" : "#aaa" }}>
                            {charsLeft} left
                        </span>
                    </div>

                    <div style={{ marginTop: 18 }}>
                        <ClassificationSelect
                            value={classification}
                            onChange={setClassification}
                            disabled={saving}
                        />
                        <p style={{ margin: "5px 0 0", fontSize: 12, color: "#aaa" }}>
                            New contacts added to this book will default to this classification.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div style={{ padding: "0 28px 24px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => !saving && onClose()}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={saving || !name.trim()}
                    >
                        {saving ? "Creating…" : "Create Book"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AddBookModal;
